import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  username: string | null;
  avatarUrl: string | null;
  usernameLoading: boolean;
  signOut: () => Promise<void>;
  updateUsername: (newUsername: string) => Promise<{ error: string | null }>;
  updateAvatar: (file: File) => Promise<{ error: string | null }>;
  refreshUsername: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ADMIN_EMAIL = 'nagayama@example.com';

function generateRandomUsername(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  let name = '';
  for (let i = 0; i < 6; i++) name += chars[Math.floor(Math.random() * chars.length)];
  for (let i = 0; i < 3; i++) name += digits[Math.floor(Math.random() * digits.length)];
  return name;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [usernameLoading, setUsernameLoading] = useState(false);

  const isAdmin = user?.email === ADMIN_EMAIL;

  const fetchOrCreateUsername = useCallback(async (uid: string) => {
    setUsernameLoading(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('username, avatar_url')
        .eq('user_id', uid)
        .maybeSingle();

      if (data?.username) {
        setUsername(data.username);
        setAvatarUrl(data.avatar_url || null);
      } else {
        let newUsername = generateRandomUsername();
        for (let i = 0; i < 5; i++) {
          const { data: existing } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('username', newUsername)
            .maybeSingle();
          if (!existing) break;
          newUsername = generateRandomUsername();
        }
        const { error } = await supabase
          .from('user_profiles')
          .insert({ user_id: uid, username: newUsername });
        if (!error) setUsername(newUsername);
      }
    } catch (e) {
      console.error('fetchOrCreateUsername error:', e);
    } finally {
      setUsernameLoading(false);
    }
  }, []);

  useEffect(() => {
    // 初回セッション取得
    supabase.auth.getSession().then(({ data: { session: s }, error }) => {
      if (error) {
        console.error('getSession error:', error);
        // リフレッシュトークンが無効な場合はローカルセッションをクリア
        if (
          error.message?.includes('Refresh Token Not Found') ||
          error.message?.includes('Invalid Refresh Token') ||
          error.message?.includes('refresh_token_not_found')
        ) {
          supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          // ローカルストレージのSupabaseキーを全削除
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('sb-')) localStorage.removeItem(key);
          });
        }
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      if (s?.user) fetchOrCreateUsername(s.user.id);
    }).catch((err) => {
      console.error('getSession error:', err);
      // 予期しないエラーでもローカルセッションをクリア
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-')) localStorage.removeItem(key);
      });
      setSession(null);
      setUser(null);
      setLoading(false);
    });

    // 認証状態変化の監視
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);

      if (event === 'SIGNED_OUT') {
        setUsername(null);
        setAvatarUrl(null);
        setLoading(false);
      } else if (event === 'SIGNED_IN') {
        setLoading(false);
        if (s?.user) fetchOrCreateUsername(s.user.id);
      } else if (event === 'TOKEN_REFRESHED') {
        if (s?.user) {
          fetchOrCreateUsername(s.user.id);
        } else {
          // トークンリフレッシュ失敗 → ローカルをクリア
          setUsername(null);
          setAvatarUrl(null);
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('sb-')) localStorage.removeItem(key);
          });
        }
        setLoading(false);
      } else if ((event as string) === 'TOKEN_REFRESH_FAILED') {
        // リフレッシュトークン失敗イベント
        setUsername(null);
        setAvatarUrl(null);
        setSession(null);
        setUser(null);
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('sb-')) localStorage.removeItem(key);
        });
        setLoading(false);
      } else {
        setLoading(false);
        if (!s) {
          setUsername(null);
          setAvatarUrl(null);
        }
      }
    });

    return () => { subscription.unsubscribe(); };
  }, [fetchOrCreateUsername]);

  const handleSignOut = async () => {
    try {
      setLoading(true);
      // セッションが存在する場合のみサーバー側のサインアウトを実行
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        const { error } = await supabase.auth.signOut();
        if (error && error.message !== 'Auth session missing!') {
          console.error('signOut error:', error);
        }
      }
    } catch (e) {
      console.error('signOut unexpected error:', e);
    } finally {
      // セッションの有無に関わらず、ローカル状態を確実にクリア
      setUser(null);
      setSession(null);
      setUsername(null);
      setAvatarUrl(null);
      setLoading(false);
    }
  };

  const updateUsername = async (newUsername: string): Promise<{ error: string | null }> => {
    if (!user) return { error: 'ログインが必要です' };
    const trimmed = newUsername.trim();
    if (!trimmed) return { error: 'ユーザーネームを入力してください' };
    if (trimmed.length < 3) return { error: '3文字以上で入力してください' };
    if (trimmed.length > 20) return { error: '20文字以内で入力してください' };
    if (!/^[a-zA-Z0-9_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]+$/.test(trimmed)) {
      return { error: '使用できない文字が含まれています（英数字・日本語・アンダースコアのみ）' };
    }

    const { data: existing } = await supabase
      .from('user_profiles')
      .select('user_id')
      .eq('username', trimmed)
      .maybeSingle();

    if (existing && existing.user_id !== user.id) {
      return { error: 'このユーザーネームは既に使用されています' };
    }

    const { error } = await supabase
      .from('user_profiles')
      .update({ username: trimmed, updated_at: new Date().toISOString() })
      .eq('user_id', user.id);

    if (error) return { error: '更新に失敗しました' };
    setUsername(trimmed);
    return { error: null };
  };

  const updateAvatar = async (file: File): Promise<{ error: string | null }> => {
    if (!user) return { error: 'ログインが必要です' };
    if (file.size > 5 * 1024 * 1024) return { error: 'ファイルサイズは5MB以下にしてください' };
    if (!file.type.startsWith('image/')) return { error: '画像ファイルを選択してください' };

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      if (avatarUrl) {
        const oldPath = avatarUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('avatars').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(fileName);
      const publicUrl = urlData.publicUrl;

      const { error: dbError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (dbError) throw dbError;

      setAvatarUrl(publicUrl);
      return { error: null };
    } catch (e) {
      console.error('updateAvatar error:', e);
      return { error: 'アバターの更新に失敗しました' };
    }
  };

  const refreshUsername = async () => {
    if (user) await fetchOrCreateUsername(user.id);
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    isAdmin,
    username,
    avatarUrl,
    usernameLoading,
    signOut: handleSignOut,
    updateUsername,
    updateAvatar,
    refreshUsername,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
